import { Link } from "@tanstack/react-router"
import { Location, Logout, Star1 } from "iconsax-reactjs"
import { BookText, Store } from "lucide-react"
import { useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Drawer, DrawerContent } from "@/components/ui/drawer"
import { Separator } from "@/components/ui/separator"
import { EFolderAssets } from "@/modules/helpers/enum"
import {
  useAuthStore,
  useCurrentUser,
  useLayoutStore,
  useNavigationLeft
} from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendLogout, sendOpenAppStoreReview } from "@/modules/helpers/utils"

export const NavigationLeft = () => {
  const { logout } = useAuthStore()
  const { user } = useCurrentUser()
  const { open, setOpen } = useNavigationLeft()
  const { insetBottom, insetTop } = useLayoutStore()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const handleLogout = () => {
    sendLogout()
    setOpen(false)
    logout()
    setConfirmOpen(false)
  }

  const navItems = [
    {
      label: "Về ZZP",
      icon: <Store size={20} />,
      to: "/about"
    },
    {
      label: "Chính sách và điều khoản",
      icon: <BookText size={20} />,
      to: "/legal"
    }
  ]

  const defaultAddress = user?.contacts?.find((contact) => contact.isDefault)
  const userLocation =
    [
      defaultAddress?.address,
      defaultAddress?.wards?.name,
      defaultAddress?.districts?.name,
      defaultAddress?.provinces?.name
    ]
      .filter(Boolean)
      .join(", ") || ""

  return (
    <>
      <Drawer open={open} onOpenChange={setOpen} direction="left">
        <DrawerContent
          className="w-full px-3 flex flex-col bg-white"
          style={{
            paddingTop: `${insetTop}px`,
            paddingBottom: `${insetBottom}px`
          }}
        >
          {/* User Profile Section */}
          <div className="p-4 pb-3">
            <div className="flex flex-col items-start gap-3">
              <Avatar className="w-24 h-24 border border-gray-200">
                <AvatarImage
                  src={getImageByNameFromS3(user?.avatarName, user?.id, EFolderAssets.AVATAR)}
                  alt={user?.username || ""}
                />
                <AvatarFallback>{user?.username?.charAt(0).toUpperCase() || ""}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col gap-1">
                <h3 className="font-bold text-base text-primary-800">{user?.username || ""}</h3>
                {userLocation && (
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <Location size={14} className="text-gray-400" />
                    <span>{userLocation}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator className="my-0" />

          {/* Navigation List */}
          <div className="flex-1 py-4 gap-2 flex flex-col">
            {navItems.map((item, idx) => (
              <Link key={idx} to={item.to} onClick={() => setOpen(false)}>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 rounded-lg px-3 h-11 text-gray-900 hover:bg-gray-50"
                  )}
                >
                  <span style={{ color: "rgba(0, 145, 170, 1)" }}>{item.icon}</span>
                  <span className="text-sm">{item.label}</span>
                </Button>
              </Link>
            ))}
            <div
              className="relative rounded-2xl p-4 overflow-hidden cursor-pointer"
              style={{
                background: "linear-gradient(135deg, #16A086 0%, #5ED5A8 100%)"
              }}
            >
              {/* Icon */}
              <div className="items-center justify-center absolute top-4 left-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex itemsCenter justifyCenter">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.8235 4.34166L13.9985 6.69165C14.1568 7.01665 14.5818 7.32498 14.9402 7.39165L17.0652 7.74165C18.4235 7.96665 18.7402 8.94998 17.7652 9.93332L16.1068 11.5917C15.8318 11.8667 15.6735 12.4083 15.7652 12.8L16.2402 14.85C16.6152 16.4667 15.7485 17.1 14.3235 16.25L12.3318 15.0667C11.9735 14.85 11.3735 14.85 11.0152 15.0667L9.02348 16.25C7.59848 17.0917 6.73183 16.4667 7.10683 14.85L7.58183 12.8C7.6735 12.4167 7.51516 11.875 7.24016 11.5917L5.58184 9.93332C4.60684 8.95832 4.9235 7.97498 6.28183 7.74165L8.40682 7.39165C8.76516 7.33331 9.19016 7.01665 9.34849 6.69165L10.5235 4.34166C11.1485 3.06666 12.1818 3.06666 12.8235 4.34166Z"
                    stroke="url(#paint0_linear_2498_3873)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66797 4.16666H1.66797"
                    stroke="url(#paint1_linear_2498_3873)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.16797 15.8333H1.66797"
                    stroke="url(#paint2_linear_2498_3873)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M2.5013 10H1.66797"
                    stroke="url(#paint3_linear_2498_3873)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2498_3873"
                      x1="5.01563"
                      y1="10.058"
                      x2="18.3314"
                      y2="10.0559"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#0091AA" />
                      <stop offset="1" stopColor="#57C785" />
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_2498_3873"
                      x1="1.66797"
                      y1="4.67053"
                      x2="6.66797"
                      y2="4.66658"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#0091AA" />
                      <stop offset="1" stopColor="#57C785" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_2498_3873"
                      x1="1.66797"
                      y1="16.3372"
                      x2="4.16797"
                      y2="16.3362"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#0091AA" />
                      <stop offset="1" stopColor="#57C785" />
                    </linearGradient>
                    <linearGradient
                      id="paint3_linear_2498_3873"
                      x1="1.66797"
                      y1="10.5039"
                      x2="2.5013"
                      y2="10.5038"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#0091AA" />
                      <stop offset="1" stopColor="#57C785" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Content */}
              <div className="mt-12">
                <h3 className="text-xl font-semibold text-white mb-2">Đánh giá chúng tôi</h3>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star1
                      variant="Linear"
                      key={star}
                      size={24}
                      color="white"
                      className="fill-yellow-400"
                    />
                  ))}
                </div>
                <p className="text-xs text-white/90 leading-relaxed my-4">
                  Ý kiến của bạn vô cùng hữu ích và giúp chúng tôi rất nhiều trong việc cải thiện
                  trải nghiệm ứng dụng của bạn.
                </p>
                <Button
                  variant="outline"
                  onClick={() => sendOpenAppStoreReview()}
                  className="w-fit bg-white rounded-xl h-10 text-primary"
                >
                  <span className="text-sm text-primary">Đánh giá ngay</span>
                </Button>

                {/* Star Rating */}
              </div>
            </div>
          </div>

          <Separator className="my-0" />

          {/* Logout Button and Version */}
          <div className="py-4 space-y-3">
            <Button
              className="w-full justify-start gap-3 rounded-lg px-3 h-11 text-red-600 hover:opacity-90"
              style={{ backgroundColor: "rgba(255, 239, 239, 1)" }}
              onClick={() => setConfirmOpen(true)}
            >
              <Logout size={20} className="text-red-600" />
              <span className="text-sm font-medium">Đăng xuất</span>
            </Button>
            <p className="text-xs text-gray-400 pl-3">Phiên bản 2.0.1</p>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Confirm Logout Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="w-4/5">
          <DialogHeader>
            <DialogTitle className="text-sm">Bạn có chắc chắn muốn đăng xuất?</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-4 ">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Hủy
            </Button>
            <Button onClick={handleLogout}>Đăng xuất</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
